import { useEffect, useRef, useState } from "react";

interface Shortcut {
  readonly keys: string;
  readonly does: string;
}

const SHORTCUTS: readonly Shortcut[] = [
  { keys: "Right-click", does: "Order, marker or fire support, right here" },
  { keys: "/", does: "Find a place" },
  { keys: "G", does: "Toggle grid" },
  { keys: "L", does: "Toggle labels" },
  { keys: "1 2 3", does: "Switch map (commander only)" },
  { keys: "Esc", does: "Cancel placing a marker" },
];

/**
 * What the keyboard does, on request.
 *
 * A shortcut nobody knows about is not a shortcut, it is a secret — and the
 * usual fix, a permanent legend on screen, is exactly the kind of decoration
 * this map has none of. So it stays completely out of the way until asked
 * for, and the button that asks for it is the only thing that has to be
 * self-explanatory.
 */
export function ShortcutsHelp(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label="Keyboard shortcuts"
        title="Keyboard shortcuts"
        className={
          open
            ? "flex h-6 w-6 items-center justify-center rounded border border-border-strong bg-surface-raised text-xs text-text-primary"
            : "flex h-6 w-6 items-center justify-center rounded border border-transparent text-xs text-text-muted transition-colors hover:text-text-secondary"
        }
      >
        ?
      </button>

      {open && (
        <div className="absolute top-full right-0 z-20 mt-1 w-56 rounded border border-border-strong bg-surface-raised p-2 shadow-lg">
          <ul className="space-y-1">
            {SHORTCUTS.map((shortcut) => (
              <li key={shortcut.keys} className="flex items-center justify-between gap-3">
                <span className="numeric rounded border border-border-subtle bg-surface-sunken px-1.5 py-0.5 text-[10px] text-text-secondary">
                  {shortcut.keys}
                </span>
                <span className="text-[11px] text-text-muted">{shortcut.does}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
